import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SearchIcon } from 'lucide-react';

import { cn } from 'common/style/utils';
import Flex from 'common/ui/Flex';
import VStack from 'common/ui/VStack';
import { forwardRefNoClassName } from 'common/uiV3/common';
import Input from 'common/uiV3/Input';
import Spinner from 'common/uiV3/Spinner';
import { isNotNil } from 'common/util/isNil';

export type SearchableListOption = {
  disabled?: boolean;
  label: React.ReactNode;
  searchLabel?: string;
  value: string;
};

type SearchableListContextValue = {
  emptyPlaceholder?: string;
  filteredOptions: SearchableListOption[];
  isLoading: boolean;
  isSearchable: boolean;
  onOptionSelect?: (option: SearchableListOption) => void;
  onSearchChange?: (search: string) => void;
  options: SearchableListOption[];
  searchPlaceholder?: string;
  selectedIndex: number;
  setFilteredOptions: (options: SearchableListOption[]) => void;
  setSelectedIndex: (index: number) => void;
};

const SearchableListContext = createContext<SearchableListContextValue | undefined>(undefined);

const useSearchableListContext = () => {
  const context = useContext(SearchableListContext);
  if (!context) {
    throw new Error('SearchableList components must be used within SearchableList');
  }
  return context;
};

const matchesSearch = (option: SearchableListOption, searchLower: string): boolean => {
  const labelMatch =
    typeof option.label === 'string' && option.label.toLowerCase().includes(searchLower);
  const searchLabelMatch = option.searchLabel?.toLowerCase().includes(searchLower);
  const valueMatch = option.value.toLowerCase().includes(searchLower);

  return labelMatch || searchLabelMatch || valueMatch;
};

const filterOptionsBySearch = (
  options: SearchableListOption[],
  searchValue: string,
): SearchableListOption[] => {
  if (!searchValue.trim()) {
    return options;
  }

  const searchLower = searchValue.toLowerCase();
  return options.filter((option) => matchesSearch(option, searchLower));
};

type SearchableListRootProps = {
  className?: string;
  emptyPlaceholder?: string;
  isLoading?: boolean;
  isSearchable?: boolean;
  onOptionSelect?: (option: SearchableListOption) => void;
  onSearchChange?: (search: string) => void;
  options: SearchableListOption[];
  searchPlaceholder?: string;
};

const SearchableListRoot = ({
  options,
  isSearchable,
  onSearchChange,
  onOptionSelect,
  isLoading = false,
  searchPlaceholder = 'Search',
  emptyPlaceholder = 'No results found',
  className,
}: SearchableListRootProps) => {
  const _isSearchable = useMemo(() => {
    if (isNotNil(isSearchable)) {
      return isSearchable;
    }
    return options.length > 5;
  }, [isSearchable, options]);

  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const contextValue = useMemo<SearchableListContextValue>(
    () => ({
      options,
      filteredOptions,
      setFilteredOptions,
      isSearchable: _isSearchable,
      onSearchChange,
      onOptionSelect,
      isLoading,
      searchPlaceholder,
      emptyPlaceholder,
      selectedIndex,
      setSelectedIndex,
    }),
    [
      options,
      filteredOptions,
      _isSearchable,
      onSearchChange,
      onOptionSelect,
      isLoading,
      searchPlaceholder,
      emptyPlaceholder,
      selectedIndex,
    ],
  );

  return (
    <SearchableListContext.Provider value={contextValue}>
      <div className={cn('bg-popover text-popover-foreground rounded-sm', className)}>
        <SearchableListContent />
      </div>
    </SearchableListContext.Provider>
  );
};

const SearchableListContent = forwardRefNoClassName<HTMLDivElement>((_, ref) => {
  const {
    isSearchable,
    onSearchChange,
    onOptionSelect,
    isLoading,
    options,
    filteredOptions,
    setFilteredOptions,
    emptyPlaceholder,
    selectedIndex,
    setSelectedIndex,
  } = useSearchableListContext();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectableOptions = useMemo(
    () => filteredOptions.filter((option) => !option.disabled),
    [filteredOptions],
  );

  const handleSearchChange = useCallback(
    (search: string) => {
      if (onSearchChange) {
        onSearchChange(search);
      } else {
        setFilteredOptions(filterOptionsBySearch(options, search));
      }
    },
    [onSearchChange, options, setFilteredOptions],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(selectedIndex < selectableOptions.length - 1 ? selectedIndex + 1 : 0);
          break;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : selectableOptions.length - 1);
          break;

        case 'Enter':
          e.preventDefault();
          if (onOptionSelect && selectableOptions[selectedIndex]) {
            onOptionSelect(selectableOptions[selectedIndex]);
          }
          break;

        default:
          if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
            searchInputRef.current.focus();
          }
          break;
      }
    },
    [selectedIndex, selectableOptions, setSelectedIndex, onOptionSelect],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div ref={ref}>
      {isSearchable && (
        <SearchableListSearchInput ref={searchInputRef} onSearch={handleSearchChange} />
      )}
      <VStack className="box-border p-1 max-h-[400px] overflow-y-auto">
        {isLoading && (
          <Flex className="justify-center items-center h-full py-3">
            <Spinner size="large" />
          </Flex>
        )}
        {!isLoading && filteredOptions.length > 0 && (
          <SearchableListOptionsList
            options={filteredOptions}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            onOptionSelect={onOptionSelect}
          />
        )}
        {!isLoading && filteredOptions.length === 0 && (
          <Flex className="justify-center items-center h-full py-3">{emptyPlaceholder}</Flex>
        )}
      </VStack>
    </div>
  );
});

const SearchableListSearchInput = forwardRef<
  HTMLInputElement,
  {
    onSearch: (search: string) => void;
  }
>(({ onSearch }, ref) => {
  const { searchPlaceholder } = useSearchableListContext();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, []);

  return (
    <div className="base-border-b p-1">
      <div className="px-2 py-1">
        <Input
          ref={ref || inputRef}
          placeholder={searchPlaceholder}
          startIcon={SearchIcon}
          variant="unstyled"
          autoFocus
          onChange={onSearch}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
});

const SearchableListOptionsList = forwardRefNoClassName<
  HTMLDivElement,
  {
    options: SearchableListOption[];
    selectedIndex?: number;
    setSelectedIndex?: (index: number) => void;
    onOptionSelect?: (option: SearchableListOption) => void;
  }
>((props, ref) => {
  let currentSelectableIndex = 0;

  return (
    <div ref={ref}>
      {props.options.map((option) => {
        const isHighlighted = !option.disabled && currentSelectableIndex === props.selectedIndex;

        if (!option.disabled) {
          currentSelectableIndex++;
        }

        return (
          <SearchableListOptionItem
            key={option.value}
            option={option}
            isHighlighted={isHighlighted}
            setSelectedIndex={props.setSelectedIndex}
            selectableIndex={currentSelectableIndex - 1}
            onOptionSelect={props.onOptionSelect}
          />
        );
      })}
    </div>
  );
});

const SearchableListOptionItem = forwardRefNoClassName<
  HTMLDivElement,
  {
    isHighlighted?: boolean;
    option: SearchableListOption;
    selectableIndex?: number;
    setSelectedIndex?: (index: number) => void;
    onOptionSelect?: (option: SearchableListOption) => void;
  }
>((props, ref) => {
  const { option, isHighlighted, selectableIndex, setSelectedIndex, onOptionSelect } = props;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // use ctrl + click to open in another tab (for posts)
      if (e.metaKey || e.ctrlKey) {
        return;
      }

      if (!option.disabled && onOptionSelect) {
        onOptionSelect(option);
      }
    },
    [option, onOptionSelect],
  );

  return (
    <Flex
      ref={ref}
      className={cn(
        'box-border hover:cursor-pointer',
        'relative flex w-full cursor-default items-center gap-2 min-w-0',
        'rounded-sm py-1.5 px-2 text-base',
        'outline-hidden select-none',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0',
        "[&_svg:not([class*='size-'])]:size-4",
        isHighlighted && 'bg-indigo-30 text-primary dark:bg-input/50 dark:text-primary-foreground',
      )}
      data-disabled={option.disabled}
      data-highlighted={isHighlighted}
      onMouseEnter={() => setSelectedIndex?.(selectableIndex ?? 0)}
      onClick={handleClick}>
      <Flex className="text-base truncate block min-w-0">{option.label}</Flex>
    </Flex>
  );
});

const SearchableList = Object.assign(SearchableListRoot, {
  Content: SearchableListContent,
  Option: SearchableListOptionItem,
  OptionsList: SearchableListOptionsList,
  SearchInput: SearchableListSearchInput,
});

export default SearchableList;
